import {
  getNasalizationFontConfig,
  enforceNasalizationInHtmlFragment,
  ensureNasalizationInHtml,
} from './nasalization-font';

export interface TemplateExample {
  id: string;
  name: string;
  subject: string;
  folderName: string;
  category: string;
  description: string;
  icon: string;
  badge: string;
  badgeColor: string;
  previewSnippet: string;
  dynamicTags: string[];
  html: string;
  design: any;
  ThumbnailHtml?: string;
}

export function cleanHtmlForUnlayer(rawHtml: string, preferredFontFamily?: string): string {
  if (!rawHtml || typeof rawHtml !== 'string') return '';
  let html = rawHtml.trim();

  // Se já for apenas um fragmento HTML limpo (sem <html> ou <body>)
  const hasHtmlTag = /<html[^>]*>/i.test(html);
  const hasBodyTag = /<body[^>]*>/i.test(html);

  if (!hasHtmlTag && !hasBodyTag) {
    const cleaned = html
      .replace(/<!DOCTYPE[^>]*>/gi, '')
      .replace(/<\/?html[^>]*>/gi, '')
      .replace(/<\/?head[^>]*>/gi, '')
      .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
      .replace(/<meta[^>]*>/gi, '')
      .replace(/<xml>[\s\S]*?<\/xml>/gi, '')
      .trim();
    return enforceNasalizationInHtmlFragment(cleaned, preferredFontFamily);
  }

  // Extrair estilos da tag <head>
  const headStyleMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
  let styles = '';
  if (headStyleMatch) {
    const styleMatches = headStyleMatch[1].match(/<style[^>]*>[\s\S]*?<\/style>/gi);
    if (styleMatches) {
      styles = styleMatches.join('\n');
    }
  }

  // Extrair o conteúdo do <body>
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  let bodyContent = bodyMatch ? bodyMatch[1] : html;

  bodyContent = bodyContent
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<\/?head[^>]*>/gi, '')
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
    .replace(/<meta[^>]*>/gi, '')
    .replace(/<xml>[\s\S]*?<\/xml>/gi, '');

  return enforceNasalizationInHtmlFragment((styles ? styles + '\n' : '') + bodyContent.trim(), preferredFontFamily);
}

export function buildUnlayerDesign(htmlContent: string, preferredFontFamily?: string): any {
  const safeHtml = cleanHtmlForUnlayer(htmlContent, preferredFontFamily);
  const fontConfig = getNasalizationFontConfig(preferredFontFamily);
  const uniqueId = () => 'u_ex_' + Math.random().toString(36).substring(2, 10);
  return {
    body: {
      rows: [
        {
          id: uniqueId(),
          cells: [1],
          columns: [
            {
              id: uniqueId(),
              contents: [
                {
                  id: uniqueId(),
                  type: 'html',
                  values: {
                    html: safeHtml,
                    containerPadding: '0px',
                    fontFamily: fontConfig,
                  },
                },
              ],
              values: {
                backgroundColor: '#ffffff',
                padding: '0px',
                border: {},
                borderRadius: '0px',
                _meta: { htmlID: uniqueId(), htmlClassNames: 'u_column' },
              },
            },
          ],
          values: {
            backgroundColor: '',
            padding: '0px',
            border: {},
            borderRadius: '0px',
            hideDesktop: false,
            hideMobile: false,
            noStackMobile: false,
            _meta: { htmlID: uniqueId(), htmlClassNames: 'u_row' },
            selectable: true,
            draggable: true,
            removable: true,
          },
        },
      ],
      values: {
        backgroundColor: '#ffffff',
        contentWidth: '600px',
        fontFamily: fontConfig,
      },
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Envio de Carta Verde (Automóvel)
// ─────────────────────────────────────────────────────────────────────────────
const HTML_CARTA_VERDE = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif;">
  <!-- Header Institucional -->
  <div style="background: linear-gradient(135deg, #1b1464 0%, #2a1f8f 100%); padding: 32px 28px; text-align: center; border-bottom: 4px solid #21f0b5;">
    <h1 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.5px;">HC — Mediação de Seguros</h1>
    <p style="color: #21f0b5; margin: 6px 0 0; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;">Seguro Automóvel &amp; Frotas</p>
  </div>

  <!-- Corpo da Mensagem -->
  <div style="padding: 32px 28px; color: #334155; line-height: 1.6; font-size: 15px;">
    <p style="font-size: 16px; margin-top: 0;">Estimado(a) <strong>{{TomadorNome}}</strong>,</p>
    <p>Esperamos que se encontre bem.</p>
    <p>Vimos por este meio enviar em anexo o <strong>Certificado Internacional de Seguro Automóvel (Carta Verde)</strong> relativo à sua apólice de seguro.</p>

    <!-- Caixa de Destaque com Detalhes -->
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #21f0b5; border-radius: 8px; padding: 18px 20px; margin: 24px 0;">
      <div style="font-size: 12px; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 10px;">Resumo da Documentação</div>
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 4px 0; color: #64748b; width: 45%;">Apólice N.º:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 700;">{{ApoliceNumero}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Ramo / Produto:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{ProdutoNome}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Tomador:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{TomadorNome}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Início de Vigência:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{DataInicio}}</td>
        </tr>
      </table>
    </div>

    <p style="font-size: 13px; color: #64748b; background: #fffbeb; border: 1px solid #fef3c7; border-radius: 6px; padding: 12px 14px;">
      💡 <strong>Recomendação:</strong> Relembramos que o documento em anexo pode ser impresso em papel branco comum ou mantido no seu dispositivo móvel para apresentação às autoridades e inspeção periódica.
    </p>

    <!-- Botão de Ação -->
    <div style="text-align: center; margin: 32px 0 24px;">
      <a href="https://segurosvirtual.pt/clientarea/login" target="_blank" style="background: #1b1464; color: #ffffff !important; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 14px; display: inline-block; box-shadow: 0 4px 12px rgba(27,20,100,0.2);">
        Aceder à Minha Área de Cliente
      </a>
    </div>

    <p style="margin-bottom: 0;">Para qualquer esclarecimento adicional ou alteração de dados, estamos inteiramente ao seu dispor.</p>
  </div>

  <!-- Assinatura do Consultor -->
  <div style="background: #f8fafc; padding: 20px 28px; border-top: 1px solid #e2e8f0; font-size: 13px; color: #64748b;">
    <div style="font-weight: 700; color: #1b1464; font-size: 14px;">{{ConsultorNome}}</div>
    <div style="color: #64748b; margin-top: 2px;">Consultor de Seguros · HC Mediação</div>
    <div style="margin-top: 6px; color: #334155;">
      <span>📞 {{ConsultorTelefone}}</span> &nbsp;|&nbsp; 
      <span>✉️ contacto@segurosvirtual.pt</span>
    </div>
  </div>

  <!-- Rodapé Institucional e Legal -->
  <div style="background: #0f0b38; color: #94a3b8; padding: 20px 28px; text-align: center; font-size: 11px; line-height: 1.5;">
    <div>HC — Mediação de Seguros, LDA · NIF 516749471</div>
    <div style="margin-top: 4px;">Inscrita na ASF sob o registo oficial de Mediação de Seguros</div>
    <div style="margin-top: 8px; color: #64748b;">Mensagem automática gerada em conformidade com o Regulamento Geral sobre a Proteção de Dados (RGPD).</div>
  </div>
</div>
`;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Aviso de Renovação de Apólice
// ─────────────────────────────────────────────────────────────────────────────
const HTML_RENOVACAO_APOLICE = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif;">
  <!-- Header -->
  <div style="background: linear-gradient(135deg, #1b1464 0%, #2a1f8f 100%); padding: 32px 28px; text-align: center; border-bottom: 4px solid #f59e0b;">
    <h1 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.5px;">HC — Mediação de Seguros</h1>
    <p style="color: #fcd34d; margin: 6px 0 0; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;">Aviso Prévio de Renovação</p>
  </div>

  <!-- Conteúdo -->
  <div style="padding: 32px 28px; color: #334155; line-height: 1.6; font-size: 15px;">
    <p style="font-size: 16px; margin-top: 0;">Estimado(a) <strong>{{TomadorNome}}</strong>,</p>
    <p>Gostaríamos de informar que se aproxima a data de renovação anual da sua apólice de seguro.</p>

    <!-- Card Detalhes da Renovação -->
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #f59e0b; border-radius: 8px; padding: 18px 20px; margin: 24px 0;">
      <div style="font-size: 12px; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 10px;">Dados da Apólice a Renovar</div>
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 4px 0; color: #64748b; width: 45%;">Produto / Ramo:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 700;">{{ProdutoNome}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">N.º de Apólice:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{ApoliceNumero}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Titular:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{TomadorNome}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Data de Vencimento:</td>
          <td style="padding: 4px 0; color: #d97706; font-weight: 700;">{{DataInicio}}</td>
        </tr>
      </table>
    </div>

    <p>Na qualidade de seus mediadores, efetuamos uma análise periódica ao mercado para garantir que beneficia sempre das <strong>melhores coberturas ao preço mais competitivo</strong>.</p>
    
    <p>Caso pretenda rever capitais, adicionar garantias (ex: proteção jurídica, assistência em viagem alargada) ou atualizar dados contratuais, estamos totalmente à sua disposição.</p>

    <!-- Botão de Ação -->
    <div style="text-align: center; margin: 32px 0 24px;">
      <a href="https://segurosvirtual.pt/clientarea/policies" target="_blank" style="background: #1b1464; color: #ffffff !important; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 14px; display: inline-block; box-shadow: 0 4px 12px rgba(27,20,100,0.2);">
        Consultar Detalhes da Apólice
      </a>
    </div>

    <p style="font-size: 13px; color: #64748b; margin-bottom: 0;">Se não pretender efetuar qualquer alteração e o pagamento for liquidado por débito direto ou entidade/referência habitual, o seu contrato renovar-se-á comodamente sem que tenha de se preocupar.</p>
  </div>

  <!-- Assinatura -->
  <div style="background: #f8fafc; padding: 20px 28px; border-top: 1px solid #e2e8f0; font-size: 13px; color: #64748b;">
    <div style="font-weight: 700; color: #1b1464; font-size: 14px;">{{ConsultorNome}}</div>
    <div style="color: #64748b; margin-top: 2px;">Gestão de Carteira &amp; Renovações · HC Mediação</div>
    <div style="margin-top: 6px; color: #334155;">📞 {{ConsultorTelefone}}</div>
  </div>

  <!-- Rodapé -->
  <div style="background: #0f0b38; color: #94a3b8; padding: 20px 28px; text-align: center; font-size: 11px; line-height: 1.5;">
    <div>HC — Mediação de Seguros, LDA · NIF 516749471</div>
    <div style="margin-top: 4px;">SegurosVirtual.pt — A sua plataforma de seguros de confiança</div>
  </div>
</div>
`;

// ─────────────────────────────────────────────────────────────────────────────
// 3. Boas-Vindas / Novo Cliente (Onboarding)
// ─────────────────────────────────────────────────────────────────────────────
const HTML_BOAS_VINDAS = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif;">
  <!-- Header -->
  <div style="background: linear-gradient(135deg, #1b1464 0%, #2a1f8f 100%); padding: 36px 28px; text-align: center; border-bottom: 4px solid #21f0b5;">
    <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">Seja Bem-vindo(a)!</h1>
    <p style="color: #21f0b5; margin: 8px 0 0; font-size: 14px; font-weight: 600;">HC — Mediação de Seguros · SegurosVirtual</p>
  </div>

  <!-- Conteúdo -->
  <div style="padding: 32px 28px; color: #334155; line-height: 1.6; font-size: 15px;">
    <p style="font-size: 16px; margin-top: 0;">Olá, <strong>{{TomadorNome}}</strong>!</p>
    <p>É com enorme satisfação que lhe damos as boas-vindas à nossa família de clientes. Agradecemos a confiança depositada na <strong>HC Mediação de Seguros</strong> para a proteção do que mais valoriza.</p>

    <!-- Bloco de Benefícios da Área de Cliente -->
    <div style="background: #f8fafc; border-radius: 10px; border: 1px solid #e2e8f0; padding: 20px; margin: 24px 0;">
      <div style="font-weight: 700; color: #1b1464; font-size: 15px; margin-bottom: 12px;">O que tem ao seu dispor no portal SegurosVirtual:</div>
      <ul style="margin: 0; padding-left: 20px; color: #475569; font-size: 14px; line-height: 1.8;">
        <li><strong>Carteira Digital Unificada:</strong> Consulte todas as suas apólices e recibos num único sítio.</li>
        <li><strong>Participação de Sinistros Online:</strong> Abertura e acompanhamento de processos em tempo real.</li>
        <li><strong>Alertas e Vencimentos:</strong> Notificações inteligentes antes de cada renovação.</li>
        <li><strong>Apoio Personalizado:</strong> Contacto direto com o seu mediador dedicado.</li>
      </ul>
    </div>

    <!-- Botão de Acesso -->
    <div style="text-align: center; margin: 32px 0 24px;">
      <a href="https://segurosvirtual.pt/clientarea/login" target="_blank" style="background: #21f0b5; color: #1b1464 !important; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: 800; font-size: 14px; display: inline-block; box-shadow: 0 4px 12px rgba(33,240,181,0.3);">
        Entrar na Área de Cliente
      </a>
    </div>

    <p style="margin-bottom: 0;">O seu consultor de referência é <strong>{{ConsultorNome}}</strong>, contactável através do telefone <strong>{{ConsultorTelefone}}</strong> para qualquer necessidade que surja.</p>
  </div>

  <!-- Rodapé -->
  <div style="background: #0f0b38; color: #94a3b8; padding: 24px 28px; text-align: center; font-size: 11px; line-height: 1.5;">
    <div>HC — Mediação de Seguros, LDA · NIF 516749471</div>
    <div style="margin-top: 4px;">Mediador de Seguros autorizado pela ASF</div>
    <div style="margin-top: 8px; color: #64748b;">segurosvirtual.pt · Sempre ao seu lado</div>
  </div>
</div>
`;

// ─────────────────────────────────────────────────────────────────────────────
// 4. Aviso de Pagamento de Prémio / Recibo
// ─────────────────────────────────────────────────────────────────────────────
const HTML_COBRANCA_PREMIO = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif;">
  <!-- Header -->
  <div style="background: linear-gradient(135deg, #1b1464 0%, #2a1f8f 100%); padding: 32px 28px; text-align: center; border-bottom: 4px solid #3b82f6;">
    <h1 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 700;">HC — Mediação de Seguros</h1>
    <p style="color: #93c5fd; margin: 6px 0 0; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;">Aviso de Pagamento de Prémio</p>
  </div>

  <!-- Conteúdo -->
  <div style="padding: 32px 28px; color: #334155; line-height: 1.6; font-size: 15px;">
    <p style="font-size: 16px; margin-top: 0;">Estimado(a) <strong>{{TomadorNome}}</strong>,</p>
    <p>Informamos que foi emitido o recibo de prémio referente à sua apólice de seguro.</p>

    <!-- Caixa de Dados de Cobrança -->
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #3b82f6; border-radius: 8px; padding: 18px 20px; margin: 24px 0;">
      <div style="font-size: 12px; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 10px;">Identificação do Seguro</div>
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 4px 0; color: #64748b; width: 45%;">Apólice:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 700;">{{ApoliceNumero}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Ramo / Produto:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{ProdutoNome}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Tomador:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{TomadorNome}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Data Limite de Pagamento:</td>
          <td style="padding: 4px 0; color: #dc2626; font-weight: 700;">{{DataInicio}}</td>
        </tr>
      </table>
    </div>

    <p style="font-size: 13px; color: #64748b; background: #eff6ff; border: 1px solid #dbeafe; border-radius: 6px; padding: 12px 14px;">
      ℹ️ O pagamento atempado do prémio é indispensável para garantir a <strong>vigência e continuidade ininterrupta de todas as coberturas</strong> contratadas.
    </p>

    <!-- Botão de Ação -->
    <div style="text-align: center; margin: 32px 0 24px;">
      <a href="https://segurosvirtual.pt/clientarea/receipts" target="_blank" style="background: #1b1464; color: #ffffff !important; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 14px; display: inline-block; box-shadow: 0 4px 12px rgba(27,20,100,0.2);">
        Consultar Dados de Pagamento
      </a>
    </div>

    <p style="margin-bottom: 0;">Se já efetuou a liquidação ou aderiu ao Débito Direto bancário, por favor ignore esta notificação.</p>
  </div>

  <!-- Assinatura -->
  <div style="background: #f8fafc; padding: 20px 28px; border-top: 1px solid #e2e8f0; font-size: 13px; color: #64748b;">
    <div style="font-weight: 700; color: #1b1464; font-size: 14px;">Departamento Financeiro · HC Mediação</div>
    <div style="margin-top: 4px;">Contacto de Apoio: {{ConsultorTelefone}}</div>
  </div>

  <!-- Rodapé -->
  <div style="background: #0f0b38; color: #94a3b8; padding: 20px 28px; text-align: center; font-size: 11px; line-height: 1.5;">
    <div>HC — Mediação de Seguros, LDA · NIF 516749471</div>
  </div>
</div>
`;

// ─────────────────────────────────────────────────────────────────────────────
// 5. Felicitações de Aniversário
// ─────────────────────────────────────────────────────────────────────────────
const HTML_ANIVERSARIO = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif;">
  <!-- Header Festivo -->
  <div style="background: linear-gradient(135deg, #1b1464 0%, #3b82f6 50%, #21f0b5 100%); padding: 40px 28px; text-align: center;">
    <div style="font-size: 44px; margin-bottom: 8px;">🎉🎂🎈</div>
    <h1 style="color: #ffffff; margin: 0; font-size: 26px; font-weight: 800; letter-spacing: -0.5px; text-shadow: 0 2px 4px rgba(0,0,0,0.2);">Parabéns!</h1>
    <p style="color: #ffffff; margin: 8px 0 0; font-size: 14px; font-weight: 600; opacity: 0.95;">HC — Mediação de Seguros</p>
  </div>

  <!-- Conteúdo -->
  <div style="padding: 36px 32px; color: #334155; line-height: 1.7; font-size: 15px; text-align: center;">
    <p style="font-size: 18px; margin-top: 0; color: #1b1464;">Estimado(a) <strong>{{TomadorNome}}</strong>,</p>
    
    <p>Hoje é o seu dia especial e não podíamos deixar de o(a) felicitar!</p>
    
    <p style="font-size: 16px; color: #475569; font-style: italic; max-width: 480px; margin: 20px auto; background: #f8fafc; padding: 20px; border-radius: 10px; border: 1px solid #e2e8f0;">
      "Desejamos-lhe um aniversário muito feliz, repleto de saúde, alegria, sucessos e momentos inesquecíveis junto de quem mais ama."
    </p>

    <p>Agradecemos a confiança e proximidade que mantém connosco todos os dias. Continuaremos sempre empenhados em proteger o seu bem-estar e o da sua família.</p>

    <!-- Botão de Visita -->
    <div style="margin: 32px 0 16px;">
      <a href="https://segurosvirtual.pt" target="_blank" style="background: #1b1464; color: #ffffff !important; text-decoration: none; padding: 12px 28px; border-radius: 8px; font-weight: 700; font-size: 14px; display: inline-block;">
        Visitar SegurosVirtual.pt
      </a>
    </div>

    <p style="font-size: 13px; color: #94a3b8; margin-top: 24px;">Com os melhores cumprimentos de toda a nossa equipa,<br><strong style="color: #1b1464;">{{ConsultorNome}} &amp; Equipa HC Mediação</strong></p>
  </div>

  <!-- Rodapé -->
  <div style="background: #0f0b38; color: #94a3b8; padding: 20px 28px; text-align: center; font-size: 11px;">
    <div>HC — Mediação de Seguros, LDA · NIF 516749471</div>
    <div style="margin-top: 4px;">Sempre presentes quando mais precisa.</div>
  </div>
</div>
`;

// ─────────────────────────────────────────────────────────────────────────────
// 6. Sinistro Automóvel (Identidade Visual Oficial Seguros Virtual - Feedback #334)
// ─────────────────────────────────────────────────────────────────────────────
const HTML_SINISTRO_AUTO_SIMPLES = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;">
  <!-- Cabeçalho com Degradê Seguros Virtual, Logótipo Branco e Slogan (Feedback #283 / #365 / #374) -->
  <div style="background: #5318b2; background-image: url('https://segurosvirtual.pt/assets/public/gradient-banner.png'); background: linear-gradient(90deg, #5318b2 0%, #0087a2 50%, #00d296 100%); background-size: cover; background-repeat: no-repeat; padding: 0;">
    <!--[if gte mso 9]>
    <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width: 600px; height: 70px;">
      <v:fill type="gradient" color="#5318b2" color2="#00d296" colors="0% #5318b2, 50% #0087a2, 100% #00d296" angle="0" />
      <v:textbox inset="0,0,0,0">
    <![endif]-->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
      <tr>
        <td align="left" style="vertical-align: middle; padding: 18px 0 18px 24px;">
          <a href="https://segurosvirtual.pt" target="_blank" style="text-decoration: none; display: inline-block;">
            <img src="https://segurosvirtual.pt/assets/logotipos/logotexto.png" alt="Seguros Virtual" height="32" style="display: block; border: 0; max-height: 32px; width: auto;" />
          </a>
        </td>
        <td align="right" style="vertical-align: middle; padding: 18px 24px 18px 0;">
          <span style="color: #ffffff; font-size: 13px; font-weight: 500; letter-spacing: 0.2px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">O futuro dos seguros começa aqui</span>
        </td>
      </tr>
    </table>
    <!--[if gte mso 9]>
      </v:textbox>
    </v:rect>
    <![endif]-->
  </div>

  <!-- Corpo do Email -->
  <div style="padding: 30px 28px 24px 28px; color: #1e293b; line-height: 1.65; font-size: 15px;">
    <p style="margin: 0 0 16px 0;">Bom dia,</p>
    <p style="margin: 0 0 16px 0;">Solicitamos a abertura do processo de sinistro.</p>
    <p style="margin: 0 0 4px 0;"><strong>Apólice:</strong> {{ApoliceNumero}}</p>
    <p style="margin: 0 0 4px 0;"><strong>Matrícula:</strong> {{Matricula}}</p>
    <p style="margin: 0 0 16px 0;"><strong>Tomador/Condutor:</strong> {{TomadorCondutor}} (NIF {{NIF}})</p>
    <p style="margin: 0 0 16px 0;">Segue em anexo a DAAA.</p>
    <p style="margin: 0 0 16px 0;">Aguardamos o número do processo.</p>
    <p style="margin: 0 0 22px 0;">Cumprimentos,</p>

    <!-- Bloco de Assinatura "Equipa Seguros Virtual" -->
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin-top: 24px; width: 100%; max-width: 440px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.03);">
      <tr>
        <td style="vertical-align: middle; width: 62px; padding-right: 14px;">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0">
            <tr>
              <td style="width: 58px; height: 58px; text-align: center; vertical-align: middle;">
                <img src="https://segurosvirtual.pt/assets/logotipos/avatar-equipa.png" width="56" height="56" style="display: block; margin: 0 auto; border: 0;" alt="Equipa Seguros Virtual" />
              </td>
            </tr>
          </table>
        </td>
        <td style="vertical-align: middle;">
          <div style="font-size: 15px; font-weight: 700; color: #1b1464; margin-bottom: 4px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">Equipa Seguros Virtual</div>
          <div style="font-size: 12px; color: #475569; margin-bottom: 3px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
            <span style="color: #00bfa5; font-weight: bold; margin-right: 4px;">✉</span> geral@segurosvirtual.pt
          </div>
          <div style="font-size: 12px; color: #475569; margin-bottom: 3px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
            <span style="color: #00bfa5; font-weight: bold; margin-right: 4px;">📞</span> +351 911 179 153
          </div>
          <div style="font-size: 12px; color: #475569; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
            <span style="color: #00bfa5; font-weight: bold; margin-right: 4px;">🌐</span> www.segurosvirtual.pt
          </div>
        </td>
      </tr>
    </table>
  </div>

  <!-- Rodapé Institucional com Degradê, Contactos, Redes Sociais e Nota Legal -->
  <div style="background: #5318b2; background-image: url('https://segurosvirtual.pt/assets/public/gradient-banner.png'); background: linear-gradient(90deg, #5318b2 0%, #0087a2 50%, #00d296 100%); background-size: cover; background-repeat: no-repeat; padding: 0; color: #ffffff;">
    <!--[if gte mso 9]>
    <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width: 600px; height: 125px;">
      <v:fill type="gradient" color="#5318b2" color2="#00d296" colors="0% #5318b2, 50% #0087a2, 100% #00d296" angle="0" />
      <v:textbox inset="0,0,0,0">
    <![endif]-->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
      <tr>
        <td align="left" style="vertical-align: top; padding: 18px 0 10px 24px; font-size: 11.5px; line-height: 1.6; color: #ffffff; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
          <div>geral@segurosvirtual.pt</div>
          <div>+351 911 179 153 (WhatsApp)</div>
          <div>segurosvirtual.pt</div>
        </td>
        <td align="right" style="vertical-align: top; padding: 18px 24px 10px 0; color: #ffffff; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
          <div style="font-size: 11px; font-weight: 600; margin-bottom: 6px; letter-spacing: 0.3px;">Segue-nos</div>
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="right" style="border-collapse: collapse;">
            <tr>
              <td style="padding: 0 4px;">
                <a href="https://facebook.com/segurosvirtual" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-facebook.png" alt="Facebook" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
              <td style="padding: 0 4px;">
                <a href="https://instagram.com/segurosvirtual" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-instagram.png" alt="Instagram" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
              <td style="padding: 0 4px;">
                <a href="https://linkedin.com/company/segurosvirtual" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-linkedin.png" alt="LinkedIn" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
              <td style="padding: 0 4px;">
                <a href="https://wa.me/351911179153" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-whatsapp.png" alt="WhatsApp" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td colspan="2" align="center" style="padding: 10px 24px 16px 24px; border-top: 1px solid rgba(255,255,255,0.25); font-size: 9.5px; color: rgba(255,255,255,0.9); line-height: 1.4; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
          © 2026 Seguros Virtual · Todos os Direitos Reservados · Registado na ASF sob nº 420573212 · Autorizado para os ramos Vida e Não Vida
        </td>
      </tr>
    </table>
    <!--[if gte mso 9]>
      </v:textbox>
    </v:rect>
    <![endif]-->
  </div>
</div>
`;

const HTML_SINISTRO_AUTO = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;">
  <!-- Header com Degradê Seguros Virtual, Logótipo Branco e Slogan (Feedback #283 / #365 / #374) -->
  <div style="background: #5318b2; background-image: url('https://segurosvirtual.pt/assets/public/gradient-banner.png'); background: linear-gradient(90deg, #5318b2 0%, #0087a2 50%, #00d296 100%); background-size: cover; background-repeat: no-repeat; padding: 0;">
    <!--[if gte mso 9]>
    <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width: 600px; height: 70px;">
      <v:fill type="gradient" color="#5318b2" color2="#00d296" colors="0% #5318b2, 50% #0087a2, 100% #00d296" angle="0" />
      <v:textbox inset="0,0,0,0">
    <![endif]-->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
      <tr>
        <td align="left" style="vertical-align: middle; padding: 18px 0 18px 24px;">
          <a href="https://segurosvirtual.pt" target="_blank" style="text-decoration: none; display: inline-block;">
            <img src="https://segurosvirtual.pt/assets/logotipos/logotexto.png" alt="Seguros Virtual" height="32" style="display: block; border: 0; max-height: 32px; width: auto;" />
          </a>
        </td>
        <td align="right" style="vertical-align: middle; padding: 18px 24px 18px 0;">
          <span style="color: #ffffff; font-size: 13px; font-weight: 500; letter-spacing: 0.2px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">O futuro dos seguros começa aqui</span>
        </td>
      </tr>
    </table>
    <!--[if gte mso 9]>
      </v:textbox>
    </v:rect>
    <![endif]-->
  </div>

  <!-- Conteúdo -->
  <div style="padding: 30px 28px 24px 28px; color: #1e293b; line-height: 1.6; font-size: 15px;">
    <p style="font-size: 16px; margin-top: 0;">Exmos. Senhores da <strong>{{Seguradora}}</strong>,</p>
    <p>Vimos por este meio apresentar a participação formal de sinistro automóvel relativo à apólice abaixo indicada, solicitando a abertura do respetivo processo e instruções para peritagem.</p>

    <!-- Caixa 1: Identificação Seguro e Viatura -->
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #1b1464; border-radius: 8px; padding: 18px 20px; margin: 20px 0;">
      <div style="font-size: 12px; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 10px;">1. Identificação do Seguro e Viatura</div>
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 4px 0; color: #64748b; width: 45%;">Apólice N.º:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 700;">{{ApoliceNumero}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Matrícula:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 700;">{{Matricula}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Seguradora:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{Seguradora}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Tomador:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{TomadorNome}} (NIF: {{TomadorNIF}})</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Condutor:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{CondutorNome}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Contacto:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{Contacto}} | {{TomadorEmail}}</td>
        </tr>
      </table>
    </div>

    <!-- Caixa 2: Ocorrência -->
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #00d296; border-radius: 8px; padding: 18px 20px; margin: 20px 0;">
      <div style="font-size: 12px; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 10px;">2. Dados da Ocorrência</div>
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 4px 0; color: #64748b; width: 45%;">Data / Hora:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{DataSinistro}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Local:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{LocalSinistro}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Circunstâncias:</td>
          <td style="padding: 4px 0; color: #334155;">{{Circunstancias}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Danos na Viatura:</td>
          <td style="padding: 4px 0; color: #dc2626; font-weight: 600;">{{Danos}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Circula:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{Circula}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Feridos / Testemunhas:</td>
          <td style="padding: 4px 0; color: #334155;">Feridos: {{Feridos}} | Testemunhas: {{Testemunhas}}</td>
        </tr>
      </table>
    </div>

    <!-- Caixa 3: Documentação -->
    <p style="font-size: 13px; color: #475569; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 12px 14px;">
      📎 <strong>Documentos em Anexo:</strong> Remetemos em anexo a Declaração Amigável (DAAA) e os registos fotográficos para abertura do processo de sinistro.
    </p>

    <p style="margin-bottom: 0;">Ficamos a aguardar a confirmação de receção e atribuição do respetivo número de processo.</p>

    <!-- Bloco de Assinatura "Equipa Seguros Virtual" -->
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin-top: 24px; width: 100%; max-width: 440px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.03);">
      <tr>
        <td style="vertical-align: middle; width: 62px; padding-right: 14px;">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0">
            <tr>
              <td style="width: 58px; height: 58px; text-align: center; vertical-align: middle;">
                <img src="https://segurosvirtual.pt/assets/logotipos/avatar-equipa.png" width="56" height="56" style="display: block; margin: 0 auto; border: 0;" alt="Equipa Seguros Virtual" />
              </td>
            </tr>
          </table>
        </td>
        <td style="vertical-align: middle;">
          <div style="font-size: 15px; font-weight: 700; color: #1b1464; margin-bottom: 4px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">Equipa Seguros Virtual</div>
          <div style="font-size: 12px; color: #475569; margin-bottom: 3px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
            <span style="color: #00bfa5; font-weight: bold; margin-right: 4px;">✉</span> geral@segurosvirtual.pt
          </div>
          <div style="font-size: 12px; color: #475569; margin-bottom: 3px; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
            <span style="color: #00bfa5; font-weight: bold; margin-right: 4px;">📞</span> +351 911 179 153
          </div>
          <div style="font-size: 12px; color: #475569; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
            <span style="color: #00bfa5; font-weight: bold; margin-right: 4px;">🌐</span> www.segurosvirtual.pt
          </div>
        </td>
      </tr>
    </table>
  </div>

  <!-- Rodapé -->
  <div style="background: #5318b2; background-image: url('https://segurosvirtual.pt/assets/public/gradient-banner.png'); background: linear-gradient(90deg, #5318b2 0%, #0087a2 50%, #00d296 100%); background-size: cover; background-repeat: no-repeat; padding: 0; color: #ffffff;">
    <!--[if gte mso 9]>
    <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width: 600px; height: 125px;">
      <v:fill type="gradient" color="#5318b2" color2="#00d296" colors="0% #5318b2, 50% #0087a2, 100% #00d296" angle="0" />
      <v:textbox inset="0,0,0,0">
    <![endif]-->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;">
      <tr>
        <td align="left" style="vertical-align: top; padding: 18px 0 10px 24px; font-size: 11.5px; line-height: 1.6; color: #ffffff; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
          <div>geral@segurosvirtual.pt</div>
          <div>+351 911 179 153 (WhatsApp)</div>
          <div>segurosvirtual.pt</div>
        </td>
        <td align="right" style="vertical-align: top; padding: 18px 24px 10px 0; color: #ffffff; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
          <div style="font-size: 11px; font-weight: 600; margin-bottom: 6px; letter-spacing: 0.3px;">Segue-nos</div>
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="right" style="border-collapse: collapse;">
            <tr>
              <td style="padding: 0 4px;">
                <a href="https://facebook.com/segurosvirtual" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-facebook.png" alt="Facebook" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
              <td style="padding: 0 4px;">
                <a href="https://instagram.com/segurosvirtual" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-instagram.png" alt="Instagram" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
              <td style="padding: 0 4px;">
                <a href="https://linkedin.com/company/segurosvirtual" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-linkedin.png" alt="LinkedIn" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
              <td style="padding: 0 4px;">
                <a href="https://wa.me/351911179153" target="_blank" style="text-decoration: none; display: inline-block;">
                  <img src="https://segurosvirtual.pt/assets/logotipos/social-whatsapp.png" alt="WhatsApp" width="22" height="22" style="display: block; border: 0;" />
                </a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td colspan="2" align="center" style="padding: 10px 24px 16px 24px; border-top: 1px solid rgba(255,255,255,0.25); font-size: 9.5px; color: rgba(255,255,255,0.9); line-height: 1.4; font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">
          © 2026 Seguros Virtual · Todos os Direitos Reservados · Registado na ASF sob nº 420573212 · Autorizado para os ramos Vida e Não Vida
        </td>
      </tr>
    </table>
    <!--[if gte mso 9]>
      </v:textbox>
    </v:rect>
    <![endif]-->
  </div>
</div>
`;

const HTML_INSPECAO_AUTOMOVEL = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden;">
  <!-- Header com Banner Gradiente -->
  <div style="background: #1b1464; background-image: url('https://segurosvirtual.pt/assets/public/gradient-banner.png'); background: linear-gradient(90deg, #1b1464 0%, #5318b2 50%, #00d296 100%); background-size: cover; padding: 20px 24px; color: #ffffff;">
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
      <tr>
        <td align="left" style="vertical-align: middle;">
          <a href="https://segurosvirtual.pt" target="_blank" style="text-decoration: none;">
            <img src="https://segurosvirtual.pt/assets/logotipos/logotexto.png" alt="Seguros Virtual" height="32" style="display: block; border: 0; max-height: 32px; width: auto;" />
          </a>
        </td>
        <td align="right" style="vertical-align: middle;">
          <span style="color: #ffffff; font-size: 13px; font-weight: 500;">🚗 Inspeção Periódica</span>
        </td>
      </tr>
    </table>
  </div>

  <!-- Conteúdo Principal -->
  <div style="padding: 30px 28px 24px 28px; color: #334155; line-height: 1.6; font-size: 15px;">
    <p style="font-size: 15px; line-height: 1.6; color: #334155; margin-top: 0;">Bom dia <strong>{{PrimeiroNome}}</strong>,</p>
    <p style="font-size: 15px; line-height: 1.6; color: #334155;">Estamos a avisar com a devida antecedência que a <strong>Inspeção Periódica obrigatória (IPO)</strong> do seu veículo com a matrícula <strong style="background: #e2e8f0; padding: 2px 6px; border-radius: 4px; color: #1b1464;">{{Matricula}}</strong> deve ser realizada até <strong>{{DiaMesPrimeiraMatricula}}</strong>.</p>
    
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #00d296; border-radius: 8px; padding: 18px 20px; margin: 20px 0;">
      <div style="font-size: 12px; text-transform: uppercase; color: #64748b; font-weight: 700; margin-bottom: 10px;">📋 Dados da Viatura</div>
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 4px 0; color: #64748b; width: 45%;">Matrícula:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 700;">{{Matricula}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Data da 1ª Matrícula:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{DataPrimeiraMatricula}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Mês da Inspeção:</td>
          <td style="padding: 4px 0; color: #0087a2; font-weight: 700;">{{DiaMesPrimeiraMatricula}}</td>
        </tr>
        <tr>
          <td style="padding: 4px 0; color: #64748b;">Marca / Modelo:</td>
          <td style="padding: 4px 0; color: #1b1464; font-weight: 600;">{{Marca}} {{Modelo}}</td>
        </tr>
      </table>
    </div>

    <p style="font-size: 15px; line-height: 1.6; font-weight: 600; color: #1b1464; margin-bottom: 8px;">Antes de levar a viatura à inspeção, vale a pena confirmar:</p>
    <ul style="padding-left: 20px; margin: 0 0 20px 0; font-size: 15px; color: #334155; line-height: 1.8;">
      <li style="font-size: 15px; color: #334155;">Luzes e sinalização (médios, máximos, piscas e stop);</li>
      <li style="font-size: 15px; color: #334155;">Pneus e travões (profundidade do piso e pressão);</li>
      <li style="font-size: 15px; color: #334155;">Triângulo de sinalização e colete retrorrefletor homologado;</li>
      <li style="font-size: 15px; color: #334155;">Estado geral da viatura e vidros;</li>
      <li style="font-size: 15px; color: #334155;">Documentos: DUA / Livrete e ficha da última inspeção.</li>
    </ul>

    <p style="font-size: 15px; line-height: 1.6; color: #334155;">Se precisar de ajuda ou tiver alguma dúvida, estamos disponíveis para o apoiar.</p>

    <!-- Call to Action -->
    <div style="text-align: center; margin: 28px 0 16px 0;">
      <a href="https://segurosvirtual.pt/login" target="_blank" style="background: #1b1464; color: #ffffff; text-decoration: none; padding: 12px 28px; border-radius: 6px; font-weight: 600; font-size: 14px; display: inline-block;">Fale Connosco</a>
    </div>

    <!-- Bloco Informativo Duplo: Sabia que? + Atenção -->
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-top: 20px;">
      <tr>
        <td style="width: 50%; vertical-align: top; padding-right: 8px;">
          <div style="background: #ede9fe; border-radius: 8px; padding: 14px 16px; font-size: 12px; color: #4c1d95; line-height: 1.5;">
            <strong>💡 Sabia que?</strong><br />
            Deixou de ser obrigatório exibir o dístico da inspeção no para-brisas. Basta juntar todos os documentos na sua viatura.
          </div>
        </td>
        <td style="width: 50%; vertical-align: top; padding-left: 8px;">
          <div style="background: #fef2f2; border-radius: 8px; padding: 14px 16px; font-size: 12px; color: #991b1b; line-height: 1.5;">
            <strong>⚠️ Atenção</strong><br />
            Circular sem a inspeção periódica válida dá origem a uma coima de <strong>250€</strong>.
          </div>
        </td>
      </tr>
    </table>
  </div>

  <!-- Rodapé -->
  <div style="background: #1C1463; padding: 20px 24px; text-align: center; font-size: 12px; color: rgba(255,255,255,0.7);">
    <p style="margin: 0 0 4px 0; color: #ffffff; font-weight: 600;">HC - Mediação de Seguros, LDA</p>
    <p style="margin: 0; font-size: 11px;">Seguros Virtual &bull; <a href="https://segurosvirtual.pt" style="color: #00d296; text-decoration: none;">www.segurosvirtual.pt</a></p>
  </div>
</div>
`;

const HTML_COMPRA_CONFIRMADA_CRM = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden;">
  <div style="background: linear-gradient(135deg, #0284c7 0%, #1b1464 100%); padding: 28px 30px; text-align: center;">
    <img src="https://segurosvirtual.pt/assets/logotipos/logotexto.png" alt="Seguros Virtual" style="max-width: 220px; height: auto; display: block; margin: 0 auto 14px auto;" />
    <h1 style="margin: 0; color: #ffffff; font-size: 22px; font-weight: 800;">A sua ferramenta Premium está ativa!</h1>
    <p style="margin: 6px 0 0 0; color: rgba(255,255,255,0.88); font-size: 13.5px; font-weight: 500;">Pagamento {{metodo_pagamento}} confirmado · Acesso imediato atribuído</p>
  </div>
  <div style="padding: 30px 32px 24px 32px; color: #334155; line-height: 1.65;">
    <p style="margin: 0 0 14px 0; font-size: 16px; font-weight: 700; color: #0f172a;">Olá {{nome_cliente}},</p>
    <p style="margin: 0 0 14px 0;">O seu pagamento de <strong>{{valor}}/{{periodicidade}}</strong> referente à opção <strong>"{{opcao}}"</strong> ({{categoria}}) foi confirmado com sucesso.</p>
    <p style="margin: 0 0 18px 0;">O acesso já foi atribuído automaticamente à sua conta e a ferramenta encontra-se totalmente desbloqueada no seu CRM Mediadores.</p>

    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 20px; margin: 22px 0;">
      <div style="margin-bottom: 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 10px;">
        <strong style="font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b;">Resumo da Subscrição CRM Mediadores</strong>
        <span style="float: right; background: #d1fae5; color: #047857; font-weight: 700; font-size: 11px; padding: 3px 10px; border-radius: 99px;">✓ PAGAMENTO CONFIRMADO</span>
      </div>
      <table width="100%" cellpadding="6" cellspacing="0" border="0" style="font-size: 13.5px; color: #1e293b;">
        <tr>
          <td style="color: #64748b; width: 42%;">Opção Adquirida:</td>
          <td style="font-weight: 700; color: #0f172a;">{{opcao}} ({{categoria}})</td>
        </tr>
        <tr>
          <td style="color: #64748b;">Modalidade / Plano:</td>
          <td style="font-weight: 700; color: #0f172a;">Subscrição {{plano}}</td>
        </tr>
        <tr>
          <td style="color: #64748b;">Referência do Pedido:</td>
          <td><code style="background: #e2e8f0; padding: 2px 8px; border-radius: 5px; font-weight: 700; color: #0f172a;">{{referencia}}</code></td>
        </tr>
        <tr>
          <td style="color: #64748b;">Método de Pagamento:</td>
          <td style="font-weight: 600;">{{metodo_pagamento}} ({{telemovel_mbway}})</td>
        </tr>
        <tr>
          <td style="color: #64748b;">NIF Associado:</td>
          <td style="font-weight: 600;">{{nif}}</td>
        </tr>
        <tr>
          <td style="color: #64748b;">Valor ({{plano}}):</td>
          <td style="font-weight: 800; font-size: 16px; color: #0284c7;">{{valor}} / {{periodicidade}}</td>
        </tr>
        <tr>
          <td style="color: #64748b;">Data/Hora:</td>
          <td style="color: #475569;">{{data_compra}}</td>
        </tr>
      </table>
    </div>

    <div style="text-align: center; margin: 28px 0 18px 0;">
      <a href="{{link_crm}}" target="_blank" style="display: inline-block; background: #0284c7; color: #ffffff; text-decoration: none; font-weight: 700; font-size: 15px; padding: 14px 32px; border-radius: 10px;">
        Aceder Agora à Ferramenta no CRM
      </a>
    </div>
  </div>
  <div style="background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 18px 30px; text-align: center;">
    <p style="margin: 0 0 6px 0; font-size: 12px; color: #64748b;">Subscrição {{plano}} de {{valor}}/{{periodicidade}} sem fidelização. Para qualquer questão contacte geral@segurosvirtual.pt.</p>
    <p style="margin: 0; font-size: 11px; color: #94a3b8;">© Seguros Virtual · CRM Mediadores</p>
  </div>
</div>
`;

const HTML_PEDIDO_PAGAMENTO_INICIADO_CRM = `
<div data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 0px; overflow: hidden;">
  <div style="background: linear-gradient(135deg, #1b1464 0%, #0284c7 100%); padding: 28px 30px; text-align: center;">
    <img src="https://segurosvirtual.pt/assets/logotipos/logotexto.png" alt="Seguros Virtual" style="max-width: 220px; height: auto; display: block; margin: 0 auto 14px auto;" />
    <h1 style="margin: 0; color: #ffffff; font-size: 22px; font-weight: 800;">Pedido de Pagamento / Subscrição Iniciado</h1>
    <p style="margin: 6px 0 0 0; color: rgba(255,255,255,0.88); font-size: 13.5px; font-weight: 500;">Confirme o pagamento ({{metodo_pagamento}}) para ativar a ferramenta</p>
  </div>
  <div style="padding: 30px 32px 24px 32px; color: #334155; line-height: 1.65;">
    <p style="margin: 0 0 14px 0; font-size: 16px; font-weight: 700; color: #0f172a;">Olá {{nome_cliente}},</p>
    <p style="margin: 0 0 14px 0;">Registámos o seu pedido de subscrição (<strong>{{plano}}</strong>) da ferramenta <strong>"{{opcao}}"</strong> ({{categoria}}) no CRM Mediadores.</p>
    <p style="margin: 0 0 18px 0;">Valor a liquidar: <strong>{{valor}}</strong> ({{metodo_pagamento}} · {{telemovel_mbway}}). Assim que o pagamento for confirmado, o acesso é atribuído de forma 100% automática.</p>

    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 20px; margin: 22px 0;">
      <div style="margin-bottom: 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 10px;">
        <strong style="font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b;">Resumo do Pedido de Subscrição</strong>
        <span style="float: right; background: #fef3c7; color: #b45309; font-weight: 700; font-size: 11px; padding: 3px 10px; border-radius: 99px;">⏳ A AGUARDAR PAGAMENTO</span>
      </div>
      <table width="100%" cellpadding="6" cellspacing="0" border="0" style="font-size: 13.5px; color: #1e293b;">
        <tr>
          <td style="color: #64748b; width: 42%;">Opção Solicitada:</td>
          <td style="font-weight: 700; color: #0f172a;">{{opcao}} ({{categoria}})</td>
        </tr>
        <tr>
          <td style="color: #64748b;">Modalidade / Plano:</td>
          <td style="font-weight: 700; color: #0f172a;">Subscrição {{plano}}</td>
        </tr>
        <tr>
          <td style="color: #64748b;">Referência do Pedido:</td>
          <td><code style="background: #e2e8f0; padding: 2px 8px; border-radius: 5px; font-weight: 700; color: #0f172a;">{{referencia}}</code></td>
        </tr>
        <tr>
          <td style="color: #64748b;">Método de Pagamento:</td>
          <td style="font-weight: 600;">{{metodo_pagamento}} ({{telemovel_mbway}})</td>
        </tr>
        <tr>
          <td style="color: #64748b;">Valor ({{plano}}):</td>
          <td style="font-weight: 800; font-size: 16px; color: #1b1464;">{{valor}} / {{periodicidade}}</td>
        </tr>
      </table>
    </div>

    <div style="text-align: center; margin: 28px 0 18px 0;">
      <a href="{{link_crm}}" target="_blank" style="display: inline-block; background: #1b1464; color: #ffffff; text-decoration: none; font-weight: 700; font-size: 15px; padding: 14px 32px; border-radius: 10px;">
        Abrir CRM Mediadores
      </a>
    </div>
  </div>
  <div style="background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 18px 30px; text-align: center;">
    <p style="margin: 0 0 6px 0; font-size: 12px; color: #64748b;">Referência do pedido: {{referencia}}. Assim que o pagamento for detetado, receberá a confirmação de desbloqueio imediato.</p>
    <p style="margin: 0; font-size: 11px; color: #94a3b8;">© Seguros Virtual · CRM Mediadores</p>
  </div>
</div>
`;

// ─────────────────────────────────────────────────────────────────────────────
// 11. Partilha de Documento (Ficha de Cliente - Apólices - Documentos - Partilhar)
// ─────────────────────────────────────────────────────────────────────────────
export const HTML_PARTILHA_DOCUMENTO = `
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" data-sv-template-root="true" data-sv-border-radius="0px" data-sv-margin-top="0px" data-sv-body-bg="#ffffff" data-sv-outer-bg="#ffffff" style="width: 100%; background-color: #ffffff; margin: 0; padding: 0; font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;">
  <tr>
    <td align="center" style="padding: 0; margin: 0;">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-collapse: collapse; border-radius: 0px; overflow: hidden;">
        <!-- Cabeçalho Oficial Seguros Virtual -->
        <tr>
          <td align="center" style="padding: 0; margin: 0; line-height: 0; font-size: 0;">
            <a href="https://segurosvirtual.pt" target="_blank" style="text-decoration: none; border: 0; display: block;">
              <img src="https://segurosvirtual.pt/assets/EmailsMarketing/cabecalho.png" alt="Seguros Virtual - O futuro dos seguros começa aqui" width="600" data-sv-fullbleed-banner="true" style="width: 100%; max-width: 600px; height: auto; display: block; border: 0; margin: 0; padding: 0;" />
            </a>
          </td>
        </tr>
        <!-- Corpo da Mensagem -->
        <tr>
          <td style="padding: 36px 40px 28px 40px; background-color: #ffffff; color: #111827; font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 16px; line-height: 1.6; text-align: left;">
            <p style="margin: 0 0 20px 0; font-size: 16px; line-height: 1.6; color: #111827;">Olá <strong>{{NomeCliente}}</strong>,</p>
            <p style="margin: 0 0 20px 0; font-size: 16px; line-height: 1.6; color: #111827;">Em resposta ao seu pedido, anexamos o documento relativo ao seu seguro.</p>
            <p style="margin: 0 0 20px 0; font-size: 16px; line-height: 1.6; color: #111827;">Pode consultar ou guardar o ficheiro em anexo.</p>
            <p style="margin: 0 0 32px 0; font-size: 16px; line-height: 1.6; color: #111827;">Obrigado por utilizar a <strong>Seguros Virtual.</strong><br />Estamos sempre disponíveis para ajudar.</p>
            <!-- Botão Aceder à Área de Cliente -->
            <div style="text-align: center; margin: 28px 0 36px 0;">
              <a href="{{LinkAreaCliente}}" target="_blank" style="display: inline-block; text-decoration: none; border: 0;">
                <img src="https://segurosvirtual.pt/assets/EmailsMarketing/botao_area_cliente.png" alt="Aceder à minha área de cliente" width="320" style="width: 100%; max-width: 320px; height: auto; display: block; margin: 0 auto; border: 0;" />
              </a>
            </div>
            <!-- Cumprimentos e Assinatura Hugo Capela -->
            <p style="margin: 0 0 16px 0; font-size: 16px; line-height: 1.6; color: #111827;">Com os melhores cumprimentos,</p>
            <div style="margin: 0; text-align: left;">
              <a href="https://segurosvirtual.pt" target="_blank" style="display: inline-block; text-decoration: none; border: 0;">
                <img src="https://segurosvirtual.pt/assets/EmailsMarketing/assinatura_hugo_capela.png" alt="Hugo Capela - Director &amp; Insurance Advisor - Seguros Virtual" width="340" style="width: 100%; max-width: 340px; height: auto; display: block; border: 0;" />
              </a>
            </div>
          </td>
        </tr>
        <!-- Rodapé Institucional -->
        <tr>
          <td align="center" style="padding: 0; margin: 0; line-height: 0; font-size: 0;">
            <a href="https://segurosvirtual.pt" target="_blank" style="text-decoration: none; border: 0; display: block;">
              <img src="https://segurosvirtual.pt/assets/EmailsMarketing/rodape_institucional.png" alt="Seguros Virtual - Rodapé Institucional" width="600" data-sv-fullbleed-banner="true" style="width: 100%; max-width: 600px; height: auto; display: block; border: 0; margin: 0; padding: 0;" />
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
`;

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTAÇÃO DO CATÁLOGO DE EXEMPLOS
// ─────────────────────────────────────────────────────────────────────────────
export const PREDEFINED_EMAIL_TEMPLATES: TemplateExample[] = [
  {
    id: 'tpl_partilha_documento',
    name: 'Partilha de Documento',
    subject: 'Documento: {{DescricaoDocumento}}',
    folderName: 'Documentos & Apólices',
    category: 'Clientes',
    description:
      'Template utilizado ao partilhar documentos de apólices na Ficha de Cliente, com cabeçalho oficial, botão de acesso à Área de Cliente, assinatura institucional e rodapé.',
    icon: 'share',
    badge: 'Documentos',
    badgeColor: '#5318b2',
    previewSnippet:
      'Olá {{NomeCliente}}, Em resposta ao seu pedido, anexamos o documento relativo ao seu seguro. Pode consultar ou guardar o ficheiro em anexo...',
    dynamicTags: [
      '{{NomeCliente}}',
      '{{PrimeiroNome}}',
      '{{NomeCompleto}}',
      '{{EmailCliente}}',
      '{{DescricaoDocumento}}',
      '{{ApoliceNumero}}',
      '{{LinkAreaCliente}}',
    ],
    html: ensureNasalizationInHtml(HTML_PARTILHA_DOCUMENTO),
    design: buildUnlayerDesign(HTML_PARTILHA_DOCUMENTO),
  },
  {
    id: 'tpl_compra_confirmada_crm',
    name: 'Compra Confirmada & Acesso Ativo (CRM Mediadores)',
    subject: '✅ Compra Confirmada: A ferramenta "{{opcao}}" já está ativa no seu CRM ({{referencia}})',
    folderName: 'Subscrições & Pagamentos',
    category: 'Subscrições & Pagamentos',
    description:
      'Email enviado automaticamente ao cliente/mediador quando o pagamento da subscrição é confirmado e a opção fica ativa no CRM.',
    icon: 'verified',
    badge: 'Pagamentos CRM',
    badgeColor: '#0284c7',
    previewSnippet:
      'Olá {{nome_cliente}}, O seu pagamento de {{valor}}/{{periodicidade}} referente à opção "{{opcao}}" ({{categoria}}) foi confirmado com sucesso...',
    dynamicTags: [
      '{{nome_cliente}}',
      '{{nif}}',
      '{{email}}',
      '{{telemovel_mbway}}',
      '{{opcao}}',
      '{{categoria}}',
      '{{plano}}',
      '{{periodicidade}}',
      '{{valor}}',
      '{{desconto}}',
      '{{metodo_pagamento}}',
      '{{referencia}}',
      '{{data_compra}}',
      '{{link_crm}}',
    ],
    html: ensureNasalizationInHtml(HTML_COMPRA_CONFIRMADA_CRM),
    design: buildUnlayerDesign(HTML_COMPRA_CONFIRMADA_CRM),
  },
  {
    id: 'tpl_pedido_pagamento_iniciado_crm',
    name: 'Pedido de Pagamento / Subscrição Iniciado (CRM Mediadores)',
    subject: '📱 Pedido de Subscrição: Desbloqueio de "{{opcao}}" ({{referencia}})',
    folderName: 'Subscrições & Pagamentos',
    category: 'Subscrições & Pagamentos',
    description:
      'Email enviado ao cliente/mediador quando inicia um pedido de subscrição ou pagamento (MB WAY, Multibanco, PayPal, Débito Direto SEPA, Cartão ou Transferência).',
    icon: 'payments',
    badge: 'Pagamentos CRM',
    badgeColor: '#1b1464',
    previewSnippet:
      'Olá {{nome_cliente}}, Registámos o seu pedido de subscrição ({{plano}}) da ferramenta "{{opcao}}" ({{categoria}}) no CRM Mediadores...',
    dynamicTags: [
      '{{nome_cliente}}',
      '{{nif}}',
      '{{email}}',
      '{{telemovel_mbway}}',
      '{{opcao}}',
      '{{categoria}}',
      '{{plano}}',
      '{{periodicidade}}',
      '{{valor}}',
      '{{desconto}}',
      '{{metodo_pagamento}}',
      '{{referencia}}',
      '{{data_compra}}',
      '{{link_crm}}',
    ],
    html: ensureNasalizationInHtml(HTML_PEDIDO_PAGAMENTO_INICIADO_CRM),
    design: buildUnlayerDesign(HTML_PEDIDO_PAGAMENTO_INICIADO_CRM),
  },
  {
    id: 'tpl_inspecoes_auto',
    name: 'Inspeções Automóvel',
    subject: 'A Inspeção do seu veículo está a aproximar-se',
    folderName: 'Automóvel',
    category: 'Automóvel',
    description:
      'Lembrete periódico de inspeção automóvel (IPO) com antecedência e verificação de pontos críticos da viatura.',
    icon: 'car_repair',
    badge: 'Automóvel',
    badgeColor: '#00d296',
    previewSnippet:
      'Bom dia {{PrimeiroNome}}! Estamos a avisar com a devida antecedência que a Inspeção Periódica obrigatória do seu veículo com a matrícula {{Matricula}} deve ser realizada até {{DiaMesPrimeiraMatricula}}...',
    dynamicTags: [
      '{{PrimeiroNome}}',
      '{{Matricula}}',
      '{{DataPrimeiraMatricula}}',
      '{{DiaMesPrimeiraMatricula}}',
      '{{Marca}}',
      '{{Modelo}}',
      '{{TomadorNome}}',
    ],
    html: ensureNasalizationInHtml(HTML_INSPECAO_AUTOMOVEL),
    design: buildUnlayerDesign(HTML_INSPECAO_AUTOMOVEL),
  },
  {
    id: 'tpl_sinistro_auto_simples',
    name: 'Sinistro Auto Simples',
    subject: 'Participação de Sinistro Auto - Apólice {{ApoliceNumero}} - Matrícula {{Matricula}}',
    folderName: 'Sinistros',
    category: 'Sinistros',
    description:
      'Participação direta e simplificada de sinistro automóvel com a identidade visual completa da Seguros Virtual.',
    icon: 'directions_car',
    badge: 'Sinistros',
    badgeColor: '#1b1464',
    previewSnippet:
      'Bom dia, Solicitamos a abertura do processo de sinistro. Apólice: {{ApoliceNumero}} Matrícula: {{Matricula}}...',
    dynamicTags: [
      '{{ApoliceNumero}}',
      '{{Matricula}}',
      '{{TomadorCondutor}}',
      '{{NomeCompleto}}',
      '{{PrimeiroNome}}',
      '{{UltimoNome}}',
      '{{PrimeiroEUltimoNome}}',
      '{{NIF}}',
    ],
    html: ensureNasalizationInHtml(HTML_SINISTRO_AUTO_SIMPLES),
    design: buildUnlayerDesign(HTML_SINISTRO_AUTO_SIMPLES),
  },
  {
    id: 'tpl_sinistro_auto',
    name: 'Sinistro Auto Completo',
    subject: 'Participação de Sinistro Auto — Apólice {{ApoliceNumero}} — Matrícula {{Matricula}}',
    folderName: 'Sinistros',
    category: 'Sinistros',
    description:
      'Participação formal detalhada de sinistro automóvel com identificação completa de seguro, circunstâncias, danos e anexos.',
    icon: 'car_crash',
    badge: 'Sinistros',
    badgeColor: '#dc2626',
    previewSnippet:
      'Exmos. Senhores da {{Seguradora}}, Vimos por este meio apresentar a participação formal de sinistro automóvel...',
    dynamicTags: [
      '{{Seguradora}}',
      '{{ApoliceNumero}}',
      '{{Matricula}}',
      '{{NomeCompleto}}',
      '{{PrimeiroNome}}',
      '{{UltimoNome}}',
      '{{TomadorNome}}',
      '{{TomadorNIF}}',
      '{{CondutorNome}}',
      '{{DataSinistro}}',
      '{{LocalSinistro}}',
      '{{Circunstancias}}',
      '{{Danos}}',
      '{{Circula}}',
    ],
    html: ensureNasalizationInHtml(HTML_SINISTRO_AUTO),
    design: buildUnlayerDesign(HTML_SINISTRO_AUTO),
  },
  {
    id: 'tpl_carta_verde',
    name: 'Envio de Carta Verde (Automóvel)',
    subject: 'Carta Verde e Documentação — Apólice {{ApoliceNumero}}',
    folderName: 'Automóvel',
    category: 'Automóvel',
    description:
      'Envio formal do Certificado Internacional de Seguro Automóvel (Carta Verde) em anexo com detalhes do contrato.',
    icon: 'directions_car',
    badge: 'Automóvel',
    badgeColor: '#1b1464',
    previewSnippet:
      'Estimado(a) {{TomadorNome}}, Vimos por este meio enviar em anexo o Certificado Internacional de Seguro Automóvel...',
    dynamicTags: ['{{TomadorNome}}', '{{ApoliceNumero}}', '{{ProdutoNome}}', '{{DataInicio}}', '{{ConsultorNome}}'],
    html: ensureNasalizationInHtml(HTML_CARTA_VERDE),
    design: buildUnlayerDesign(HTML_CARTA_VERDE),
  },
  {
    id: 'tpl_renovacao_apolice',
    name: 'Aviso de Renovação de Apólice',
    subject: 'Aviso de Renovação da sua Apólice {{ProdutoNome}} — HC Mediação',
    folderName: 'Renovações',
    category: 'Renovações',
    description:
      'Lembrete de renovação anual com resumo de garantias, prazo de vencimento e proposta de atualização de coberturas.',
    icon: 'autorenew',
    badge: 'Renovações',
    badgeColor: '#d97706',
    previewSnippet:
      'Gostaríamos de informar que se aproxima a data de renovação anual da sua apólice {{ProdutoNome}}...',
    dynamicTags: ['{{TomadorNome}}', '{{ApoliceNumero}}', '{{ProdutoNome}}', '{{DataInicio}}', '{{ConsultorNome}}'],
    html: ensureNasalizationInHtml(HTML_RENOVACAO_APOLICE),
    design: buildUnlayerDesign(HTML_RENOVACAO_APOLICE),
  },
  {
    id: 'tpl_boas_vindas',
    name: 'Boas-Vindas a Novo Cliente',
    subject: 'Bem-vindo(a) à HC - Mediação de Seguros!',
    folderName: 'Clientes',
    category: 'Clientes',
    description:
      'Acolhimento de novos clientes, introdução à equipa de mediação e convite de ativação no portal SegurosVirtual.',
    icon: 'handshake',
    badge: 'Clientes',
    badgeColor: '#059669',
    previewSnippet:
      'Olá, {{TomadorNome}}! É com enorme satisfação que lhe damos as boas-vindas à nossa família de clientes...',
    dynamicTags: ['{{TomadorNome}}', '{{ProdutoNome}}', '{{ConsultorNome}}', '{{ConsultorTelefone}}'],
    html: ensureNasalizationInHtml(HTML_BOAS_VINDAS),
    design: buildUnlayerDesign(HTML_BOAS_VINDAS),
  },
  {
    id: 'tpl_cobranca_premio',
    name: 'Aviso de Pagamento / Recibo de Prémio',
    subject: 'Aviso de Pagamento de Prémio — Apólice {{ApoliceNumero}}',
    folderName: 'Financeiro',
    category: 'Financeiro',
    description:
      'Notificação de emissão de recibo com data limite de liquidação para manutenção ininterrupta de garantias.',
    icon: 'receipt_long',
    badge: 'Financeiro',
    badgeColor: '#2563eb',
    previewSnippet:
      'Informamos que foi emitido o recibo de prémio referente à sua apólice de seguro {{ApoliceNumero}}...',
    dynamicTags: ['{{TomadorNome}}', '{{ApoliceNumero}}', '{{ProdutoNome}}', '{{DataInicio}}', '{{ConsultorNome}}'],
    html: ensureNasalizationInHtml(HTML_COBRANCA_PREMIO),
    design: buildUnlayerDesign(HTML_COBRANCA_PREMIO),
  },
  {
    id: 'tpl_aniversario',
    name: 'Felicitações de Aniversário',
    subject: 'Parabéns {{TomadorNome}}! A HC Seguros deseja-lhe um dia muito feliz 🎉',
    folderName: 'Geral',
    category: 'Geral',
    description:
      'Mensagem comemorativa e personalizada de aniversário da equipa HC Mediação para estreitar a relação com o cliente.',
    icon: 'cake',
    badge: 'Geral',
    badgeColor: '#7c3aed',
    previewSnippet:
      'Estimado(a) {{TomadorNome}}, Hoje é o seu dia especial e não podíamos deixar de o(a) felicitar!...',
    dynamicTags: ['{{TomadorNome}}', '{{ConsultorNome}}'],
    html: ensureNasalizationInHtml(HTML_ANIVERSARIO),
    design: buildUnlayerDesign(HTML_ANIVERSARIO),
  },
];
